import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { CalendarDays, Plus, Share2, Trash2, Upload, Users, Check, Copy, Pencil, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString('en-IN', {
      day: 'numeric', month: 'short', year: 'numeric',
    });
  } catch (_) { return iso; }
};

const EventsPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [uploadingPoster, setUploadingPoster] = useState(false);
  const fileInputRef = useRef(null);

  const blankForm = {
    name: '', description: '',
    event_date: '',
    is_all_day: true,
    start_time: '', end_time: '',
    poster_url: '',
  };
  const [form, setForm] = useState(blankForm);

  const loadEvents = useCallback(async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/events`);
      setEvents(r.data || []);
    } catch (e) {
      toast.error('Failed to load events', { description: e?.response?.data?.detail || e?.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  const loadRegistrations = useCallback(async (eventId) => {
    try {
      const r = await axios.get(`${API}/events/${eventId}/registrations`);
      setRegistrations(r.data || []);
    } catch (e) {
      toast.error('Failed to load registrations', { description: e?.response?.data?.detail });
    }
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(blankForm);
    setDialogOpen(true);
  };
  const openEdit = (ev) => {
    setEditingId(ev.id);
    setForm({
      name: ev.name || '',
      description: ev.description || '',
      event_date: ev.event_date || '',
      is_all_day: !!ev.is_all_day,
      start_time: ev.start_time || '',
      end_time: ev.end_time || '',
      poster_url: ev.poster_url || '',
    });
    setDialogOpen(true);
  };

  const submitEvent = async () => {
    if (!form.name.trim()) { toast.error('Event name is required'); return; }
    if (!form.event_date) { toast.error('Date is required'); return; }
    if (!form.is_all_day && !form.start_time) { toast.error('Start time required when not all-day'); return; }
    try {
      if (editingId) {
        await axios.put(`${API}/events/${editingId}`, form);
        toast.success('Event updated');
      } else {
        await axios.post(`${API}/events`, form);
        toast.success('Event created');
      }
      setDialogOpen(false);
      await loadEvents();
    } catch (e) {
      toast.error('Failed to save', { description: e?.response?.data?.detail || e?.message });
    }
  };

  const deleteEvent = async (ev) => {
    if (!window.confirm(`Delete "${ev.name}" and all its registrations? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/events/${ev.id}`);
      toast.success('Event deleted');
      setSelectedEvent(null);
      await loadEvents();
    } catch (e) {
      toast.error('Delete failed', { description: e?.response?.data?.detail });
    }
  };

  const openDetail = async (ev) => {
    setSelectedEvent(ev);
    await loadRegistrations(ev.id);
  };

  const copyShareLink = (ev) => {
    const url = `${window.location.origin}/event/${ev.share_code}`;
    navigator.clipboard?.writeText(url).then(
      () => toast.success('Share link copied', { description: url }),
      () => toast.error('Copy failed — copy manually: ' + url),
    );
  };

  const shareEvent = async (ev) => {
    const url = `${window.location.origin}/event/${ev.share_code}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: ev.name, text: `Register for ${ev.name}`, url });
      } catch (_) { /* user cancelled */ }
    } else {
      copyShareLink(ev);
    }
  };

  const uploadPoster = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Poster too large (max 5 MB)'); return; }
    setUploadingPoster(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/uploads/event-poster`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm((f) => ({ ...f, poster_url: r.data.poster_url }));
      toast.success('Poster uploaded');
    } catch (e) {
      toast.error('Upload failed', { description: e?.response?.data?.detail || e?.message });
    } finally {
      setUploadingPoster(false);
    }
  };

  const toggleCheckIn = async (reg) => {
    try {
      const path = reg.is_checked_in ? 'undo-checkin' : 'checkin';
      await axios.post(`${API}/events/${selectedEvent.id}/registrations/${reg.id}/${path}`);
      await loadRegistrations(selectedEvent.id);
    } catch (e) {
      toast.error('Check-in failed', { description: e?.response?.data?.detail });
    }
  };

  return (
    <Layout>
      <div className="p-6 lg:p-10 max-w-6xl mx-auto pb-32" data-testid="events-page">
        <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
          <div>
            <h1
              className="text-3xl lg:text-5xl font-extrabold text-white"
              style={{ fontFamily: 'Outfit' }}
            >
              Events
            </h1>
            <p className="text-gray-400 text-sm mt-1">
              Promote and run venue events — guests register through your shareable link.
            </p>
          </div>
          <Button
            onClick={openCreate}
            className="bg-[#FF007F] hover:bg-[#cc0066] text-white font-bold tracking-wider rounded-full px-6"
            data-testid="events-create-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> NEW EVENT
          </Button>
        </div>

        {loading ? (
          <p className="text-gray-500">Loading…</p>
        ) : events.length === 0 ? (
          <div
            className="bg-white/[0.03] border border-white/10 rounded-3xl p-12 text-center"
            data-testid="events-empty-state"
          >
            <CalendarDays className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-white font-bold text-lg mb-1">No events yet</p>
            <p className="text-gray-500 text-sm mb-6">Create your first event to start collecting registrations.</p>
            <Button onClick={openCreate} className="bg-[#FF007F] hover:bg-[#cc0066] text-white rounded-full">
              <Plus className="w-4 h-4 mr-2" /> CREATE EVENT
            </Button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {events.map((ev) => (
              <button
                key={ev.id}
                onClick={() => openDetail(ev)}
                className="bg-white/[0.03] border border-white/10 hover:border-[#FF007F]/50 rounded-2xl p-5 text-left transition-colors group"
                data-testid={`event-card-${ev.id}`}
              >
                {ev.poster_url ? (
                  <img
                    src={ev.poster_url}
                    alt={ev.name}
                    className="w-full aspect-video object-cover rounded-xl mb-3 border border-white/5"
                  />
                ) : (
                  <div className="w-full aspect-video rounded-xl bg-gradient-to-br from-[#FF007F]/30 to-[#9D00FF]/30 flex items-center justify-center mb-3 border border-white/5">
                    <CalendarDays className="w-10 h-10 text-white/40" />
                  </div>
                )}
                <p className="text-white font-bold text-lg truncate group-hover:text-[#FF007F] transition-colors">{ev.name}</p>
                <p className="text-gray-400 text-xs mt-1">
                  {formatDate(ev.event_date)} {!ev.is_all_day && ev.start_time ? `· ${ev.start_time}` : ''}
                  {ev.is_all_day ? ' · All day' : ''}
                </p>
                <p className="text-[#00FFFF] text-xs font-semibold mt-3 flex items-center gap-1">
                  <Users className="w-3.5 h-3.5" />
                  {ev.registration_count || 0} registered
                  {ev.checked_in_count > 0 && (
                    <span className="text-[#39FF14] ml-1">· {ev.checked_in_count} in</span>
                  )}
                </p>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0a0a0a] border border-white/10 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="event-form-dialog">
          <DialogHeader>
            <DialogTitle className="text-white" style={{ fontFamily: 'Outfit' }}>
              {editingId ? 'Edit event' : 'New event'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-gray-300 text-xs uppercase tracking-wider">Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Bollywood Night"
                className="bg-black/40 border-white/10 text-white"
                data-testid="event-form-name"
              />
            </div>
            <div>
              <Label className="text-gray-300 text-xs uppercase tracking-wider">Description (optional)</Label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="DJ Aman spinning Bollywood retro & hip-hop. Cover ₹500."
                rows={3}
                className="bg-black/40 border border-white/10 text-white rounded-md w-full px-3 py-2 text-sm placeholder:text-gray-600"
                data-testid="event-form-description"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-300 text-xs uppercase tracking-wider">Date</Label>
                <Input
                  type="date"
                  value={form.event_date}
                  onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                  className="bg-black/40 border-white/10 text-white"
                  data-testid="event-form-date"
                />
              </div>
              <div className="flex items-end gap-2 pb-2">
                <Switch
                  checked={form.is_all_day}
                  onCheckedChange={(v) => setForm({ ...form, is_all_day: v })}
                  data-testid="event-form-all-day"
                />
                <Label className="text-gray-300 text-xs uppercase tracking-wider">All day</Label>
              </div>
            </div>
            {!form.is_all_day && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-gray-300 text-xs uppercase tracking-wider">Start</Label>
                  <Input
                    type="time"
                    value={form.start_time}
                    onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                    className="bg-black/40 border-white/10 text-white"
                    data-testid="event-form-start"
                  />
                </div>
                <div>
                  <Label className="text-gray-300 text-xs uppercase tracking-wider">End (optional)</Label>
                  <Input
                    type="time"
                    value={form.end_time}
                    onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                    className="bg-black/40 border-white/10 text-white"
                    data-testid="event-form-end"
                  />
                </div>
              </div>
            )}
            <div>
              <Label className="text-gray-300 text-xs uppercase tracking-wider">Poster</Label>
              {form.poster_url ? (
                <div className="relative mt-2">
                  <img src={form.poster_url} alt="Poster preview" className="w-full max-h-48 object-cover rounded-lg border border-white/10" />
                  <Button
                    type="button"
                    onClick={() => setForm({ ...form, poster_url: '' })}
                    variant="ghost"
                    className="absolute top-2 right-2 bg-black/60 hover:bg-black/80 text-white rounded-full w-8 h-8 p-0"
                    data-testid="event-form-poster-remove"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full border border-dashed border-white/20 hover:border-[#FF007F] text-gray-400 hover:text-white rounded-lg py-8"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingPoster}
                  data-testid="event-form-poster-upload"
                >
                  <Upload className="w-4 h-4 mr-2" /> {uploadingPoster ? 'Uploading…' : 'Upload poster'}
                </Button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => { uploadPoster(e.target.files?.[0]); e.target.value = ''; }}
              />
            </div>
          </div>
          <div className="flex gap-2 justify-end mt-6">
            <Button variant="ghost" onClick={() => setDialogOpen(false)} className="text-gray-400">CANCEL</Button>
            <Button
              onClick={submitEvent}
              className="bg-[#FF007F] hover:bg-[#cc0066] text-white font-bold rounded-full px-6"
              data-testid="event-form-submit"
            >
              {editingId ? 'SAVE' : 'CREATE'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!selectedEvent} onOpenChange={(o) => !o && setSelectedEvent(null)}>
        <DialogContent className="bg-[#0a0a0a] border border-white/10 max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="event-detail-dialog">
          {selectedEvent && (
            <>
              <DialogHeader>
                <DialogTitle className="text-white text-2xl" style={{ fontFamily: 'Outfit' }}>
                  {selectedEvent.name}
                </DialogTitle>
              </DialogHeader>
              {selectedEvent.poster_url && (
                <img
                  src={selectedEvent.poster_url}
                  alt={selectedEvent.name}
                  className="w-full max-h-64 object-cover rounded-xl border border-white/10 mb-4"
                />
              )}
              <p className="text-gray-300 text-sm mb-1">
                <CalendarDays className="w-4 h-4 inline mr-1.5 text-[#FF007F]" />
                {formatDate(selectedEvent.event_date)}
                {selectedEvent.is_all_day ? ' · All day' : (selectedEvent.start_time ? ` · ${selectedEvent.start_time}` : '')}
                {selectedEvent.end_time && !selectedEvent.is_all_day ? ` – ${selectedEvent.end_time}` : ''}
              </p>
              {selectedEvent.description && (
                <p className="text-gray-400 text-sm mb-4 whitespace-pre-line">{selectedEvent.description}</p>
              )}

              <div className="flex gap-2 flex-wrap mb-6">
                <Button onClick={() => shareEvent(selectedEvent)} className="bg-[#00FFFF] hover:bg-[#00cccc] text-black font-bold rounded-full" data-testid="event-share-btn">
                  <Share2 className="w-4 h-4 mr-2" /> SHARE
                </Button>
                <Button onClick={() => copyShareLink(selectedEvent)} variant="ghost" className="border border-white/10 text-gray-300 hover:text-white rounded-full" data-testid="event-copy-link">
                  <Copy className="w-4 h-4 mr-2" /> COPY LINK
                </Button>
                <Button onClick={() => { setSelectedEvent(null); openEdit(selectedEvent); }} variant="ghost" className="border border-white/10 text-gray-300 hover:text-white rounded-full" data-testid="event-edit-btn">
                  <Pencil className="w-4 h-4 mr-2" /> EDIT
                </Button>
                <Button onClick={() => deleteEvent(selectedEvent)} variant="ghost" className="border border-[#FF3B3B]/30 text-[#FF3B3B] hover:bg-[#FF3B3B]/10 rounded-full" data-testid="event-delete-btn">
                  <Trash2 className="w-4 h-4 mr-2" /> DELETE
                </Button>
              </div>

              <h3 className="text-white font-bold uppercase tracking-wider text-sm mb-3 flex items-center gap-2">
                <Users className="w-4 h-4 text-[#00FFFF]" />
                Registrations ({registrations.length})
              </h3>
              {registrations.length === 0 ? (
                <p className="text-gray-500 text-sm py-6 text-center border border-white/5 rounded-lg">No registrations yet. Share the link to start collecting RSVPs.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/5">
                      <TableHead className="text-gray-400">Name</TableHead>
                      <TableHead className="text-gray-400">Phone</TableHead>
                      <TableHead className="text-gray-400">Registered</TableHead>
                      <TableHead className="text-gray-400 text-right">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {registrations.map((r) => (
                      <TableRow key={r.id} className="border-white/5" data-testid={`event-reg-${r.id}`}>
                        <TableCell className="text-white">{r.guest_name}</TableCell>
                        <TableCell className="text-gray-300 font-mono text-xs">{r.guest_phone}</TableCell>
                        <TableCell className="text-gray-500 text-xs">{new Date(r.created_at).toLocaleString()}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            onClick={() => toggleCheckIn(r)}
                            variant="ghost"
                            className={r.is_checked_in
                              ? 'bg-[#39FF14]/15 text-[#39FF14] border border-[#39FF14]/30 hover:bg-[#39FF14]/25'
                              : 'border border-white/10 text-gray-300 hover:text-white'}
                            data-testid={`event-reg-checkin-${r.id}`}
                          >
                            <Check className="w-3.5 h-3.5 mr-1" /> {r.is_checked_in ? 'CHECKED IN' : 'CHECK IN'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default EventsPage;
